<template>
  <div
    @focus="focus() && $emit('focus', $event)"
    @blur="blur() && $emit('blur', $event)"
    @click="$emit('click', $event)"
    @mouseover="$emit('mouseover', $event)"
    @mouseleave="$emit('mouseleave', $event)"
    :class="{
      'input-size-s': size === 's',
      'input-size-m': size === 'm' && !variableHeight,
      'input-min-size-m': size === 'm' && variableHeight,
      'variable-width': variableWidth
    }"
  >
    <label class="label">
      {{ label }}
      <span v-if="optional">({{ $t('general.optional') }})</span>
      <n-tooltip v-if="info" :tippyProps="{ content: info }" class="info-icon">
        <n-icon icon="information-circle" size="s" />
      </n-tooltip>
    </label>
    <fieldset
      :class="{
        disabled,
        errored: Boolean(error),
        active: active,
        'variable-width': variableWidth,
        'non-interactive': nonInteractive,
        'reduce-padding': buttonRightLabel
      }"
      @click="onClick"
    >
      <div v-if="imageLeft" class="image-left">
        <img v-if="isImageLeftUrl" class="image-left-url" :src="imageLeft" @error="$emit('imageLeftError')" />
        <div v-else class="image-left-placeholder">
          {{ imageLeft }}
        </div>
      </div>
      <n-icon v-else-if="iconLeft" class="icon-left" :icon="iconLeft" size="s" @click="onClickIconLeft" />
      <NResizable class="resizable-input" ref="resizable">
        <slot></slot>
        <input
          :value="value"
          :placeholder="placeholder"
          :type="type === 'password' && passwordReveal && doReveal ? 'text' : type"
          ref="input"
          :class="{ 'full-width': emptySlot }"
          :disabled="disabled"
          :readonly="readonly"
          :autocomplete="autocomplete"
          :maxlength="maxlength"
          @input="readonly ? null : $emit('input', $event.target.value)"
          @change="readonly ? null : $emit('change', $event.target.value)"
          @keydown="
            readonly ? $event.preventDefault() : $emit('keydown', $event) && nonInteractive && $event.preventDefault()
          "
          @keyup="$emit('keyup', $event)"
          @focus="$emit('focus', $event)"
          @blur="$emit('blur', $event)"
        />
      </NResizable>
      <n-icon v-if="_iconRight" class="icon-right" :icon="_iconRight" size="s" @click="onClickiconRight" />
      <n-button
        v-if="buttonRightLabel"
        class="button-right"
        :label="buttonRightLabel"
        size="s"
        @click="onClickButton"
      />
    </fieldset>
    <div v-if="error || helperText || showMaxlengthCounter" class="helper-row">
      <div v-if="error" class="helper-text error">
        {{ error }}
      </div>
      <div v-else-if="helperText" class="helper-text">
        {{ helperText }}
      </div>
      <div v-if="showMaxlengthCounter" class="helper-text maxlength">{{ length }}/{{ maxlength }}</div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'
import NResizable from '@/modules/global/components/core/NResizable.vue'

export type InputSize = 's' | 'm'

@Component({
  name: 'n-input',
  components: {
    NResizable
  }
})
export default class NInput extends Vue {
  @Prop({ default: undefined }) value?: HTMLInputElement['value']
  @Prop({ default: undefined }) label?: string
  @Prop({ default: false, type: Boolean }) optional?: boolean
  @Prop({ default: '' }) placeholder!: HTMLInputElement['placeholder']
  @Prop({ default: undefined }) autocomplete?: HTMLInputElement['autocomplete']
  @Prop({ default: '' }) info!: string

  @Prop({ default: undefined, type: Boolean }) disabled?: HTMLInputElement['disabled']
  @Prop({ default: false, type: Boolean }) readonly?: boolean
  @Prop({ default: undefined, type: String, required: false }) error?: string
  @Prop({ default: undefined, type: String, required: false }) helperText?: string

  @Prop({ default: 'text' }) type!: HTMLInputElement['type']
  @Prop({ default: false, type: Boolean }) passwordReveal!: boolean

  @Prop({ default: 'm' }) size!: InputSize
  @Prop({ default: undefined }) iconRight?: string
  @Prop({ default: undefined }) iconLeft?: string
  @Prop({ default: undefined }) imageLeft?: string
  @Prop({ default: undefined }) buttonRightLabel?: string

  @Prop({ default: undefined, type: Boolean }) active?: boolean
  @Prop({ default: false, type: Boolean }) variableHeight!: boolean
  @Prop({ default: false, type: Boolean }) variableWidth!: boolean

  @Prop({ default: undefined, type: Boolean }) nonInteractive?: boolean

  @Prop({ default: undefined, type: Boolean }) autofocus?: boolean
  @Prop({ default: undefined, type: Number }) maxlength?: number

  @Ref('input') inputElement!: HTMLInputElement

  private doReveal = false

  private get _iconRight(): string | undefined {
    if (this.type === 'password' && this.passwordReveal) {
      if (this.doReveal) {
        return 'eye-solid'
      } else {
        return 'eye-off-solid'
      }
    }

    return this.iconRight
  }

  private get emptySlot(): boolean {
    return Object.keys(this.$slots).length == 0
  }

  get isImageLeftUrl() {
    return this.imageLeft && this.imageLeft.length !== 1
  }

  get length(): number {
    return this.value?.length ?? 0
  }

  get showMaxlengthCounter(): boolean {
    return this.maxlength !== undefined && !this.readonly && !this.disabled
  }

  mounted() {
    if (this.autofocus)
      this.$nextTick(() => {
        this.inputElement.focus()
      })
  }

  onClick() {
    this.focus()
  }

  onClickiconRight(event?: MouseEvent) {
    if (this.type === 'password' && this.passwordReveal) {
      this.doReveal = !this.doReveal
    }
    this.$emit('clickiconRight', event)
  }

  onClickIconLeft() {
    this.$emit('clickIconLeft')
  }

  onClickButton() {
    this.$emit('buttonRightClick')
  }

  focus(options?: Parameters<HTMLElement['focus']>[0]) {
    if (!this.disabled) {
      this.inputElement.focus(options)
    }
  }

  isFocused(): boolean {
    return this.inputElement === document.activeElement
  }

  blur() {
    this.inputElement.blur()
  }
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles' as *;

.variable-width {
  width: 100%;
}

label {
  @include notch-text-label-light-XS;
  display: block;
  color: $notch-color-neutral-sand-600;
  padding: 0 $notch-space-4x;
  margin-bottom: $notch-space-1x;
}

fieldset {
  position: relative;
  box-sizing: border-box;

  outline: 2px solid $notch-color-neutral-sand-200;
  border-radius: $notch-radius-10x;
  padding: $notch-space-2_5x $notch-space-4x;
  min-width: $notch-space-60x;
  display: flex;
  justify-content: center;
  align-items: center;
  background: $notch-color-neutral-grey-000;
  @include notch-style-focus-outline-base;

  &.variable-width {
    min-width: unset;
    width: unset;
  }

  &.reduce-padding {
    padding: $notch-space-1_5x $notch-space-1_5x $notch-space-1_5x $notch-space-4x;
  }

  &:hover {
    outline-color: $notch-color-neutral-sand-300;
  }

  &.active {
    border-color: $notch-color-neutral-sand-400;
  }

  &:focus-within {
    @include notch-style-focus-outline;
    outline-color: $notch-color-neutral-sand-300;
  }

  // The sand-300 fill (rather than the old sand-100) is what makes "disabled" obvious at a glance,
  // so the text does not have to be faint to carry that meaning as well. Several disabled inputs
  // exist purely to *show* a value the user needs to read — the invite email on Register, the email
  // on ProfileSettings — and sand-500 on sand-300 is 1.43:1, effectively unreadable. grey-700 is
  // 4.23:1 on the same fill while still reading as inactive.
  &.disabled {
    outline-color: $notch-color-neutral-sand-300;
    background-color: $notch-color-neutral-sand-300;
    color: $notch-color-neutral-grey-700;

    .icon-left {
      color: $notch-color-neutral-grey-700;
    }

    .icon-right {
      color: $notch-color-neutral-grey-700;
    }
  }

  &.errored {
    outline-color: $notch-color-semantic-alert-400;
  }
}

.icon-left {
  margin-right: $notch-space-2x;
  color: $notch-color-neutral-sand-600;
}

.image-left {
  height: $notch-space-4x;
  width: $notch-space-4x;
  margin-right: $notch-space-2x;
}

.image-left-url {
  height: $notch-space-4x;
  width: $notch-space-4x;
  border-radius: $notch-radius-full;
  background-size: contain;
}

.image-left-placeholder {
  @include notch-text-paragraph-regular-S;
  background-color: $notch-color-neutral-sand-400;
  width: $notch-space-4x;
  height: $notch-space-4x;
  border-radius: $notch-radius-full;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-right {
  margin-left: $notch-space-2x;
  color: $notch-color-neutral-sand-600;
}

.button-right {
  margin-left: $notch-space-2x;
}

input {
  @include notch-text-label-light-M;

  border: none;
  padding: 0;
  margin: 0;
  outline: none;
  width: fit-content;
  box-sizing: border-box;
  background-color: transparent;
  color: $notch-color-neutral-grey-900;
  cursor: text;

  &::placeholder {
    color: $notch-color-neutral-sand-500;
  }

  &.full-width {
    width: 100%;
  }
  &:disabled {
    cursor: default;
  }

  @include mobile {
    @include notch-text-label-light-L;
  }
}

.non-interactive {
  pointer-events: none;
}

.helper-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: $notch-space-2x;
  margin-top: $notch-space-1x;
  padding: 0 $notch-space-4x;
}

.helper-text {
  @include notch-text-label-light-XS;
  color: $notch-color-neutral-sand-600;
  min-width: 0;

  &:not(.maxlength) {
    flex: 1;
  }

  &.error {
    color: $notch-color-semantic-alert-500;
  }

  &.maxlength {
    flex-shrink: 0;
    margin-left: auto;
  }
}

.input-size-s {
  fieldset {
    padding: $notch-space-2x $notch-space-4x;
  }

  input {
    @include notch-text-label-light-S;
  }
}

.resizable-input ::v-deep {
  width: 100%;
  .content {
    display: flex;
    flex-wrap: wrap;
    gap: $notch-space-1x;
  }
}

.label {
  display: flex;
  gap: $notch-space-1_5x;

  .info-icon {
    color: $notch-color-neutral-sand-600;
  }
}
</style>
